/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

// override to move some data here to follow the structure and style for order information and change some structure to be similler with the my account details pages

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadOrderByOrderNumber from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrderByOrderNumber";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import OrderConfirmationBillingInformation, {
    OrderConfirmationBillingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationBillingInformation";
import OrderConfirmationShippingInformation, {
    OrderConfirmationShippingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationShippingInformation";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import Notify from "@monsma/XDL/Components/Notify/Notify";
import React, { FC , useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCartState(state, state.pages.orderConfirmation.cartId);
    return {
        cartState,
        billToState: getBillToState(state, cartState.value?.billToId),
        shipToState: getShipToState(state, cartState.value?.shipToId),
        orderState: getOrderState(state, cartState.value?.orderNumber),
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        language: state.context.session.language,
    };
};

const mapDispatchToProps = {
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
};

// xng-custom XStyle injection, controlled from extentions
export interface XStyles {
    shippingInformationCarrierGridItem?: GridItemProps;
    shippingCarrierTitle?: TypographyPresentationProps;
    shippingCarrierText?: TypographyPresentationProps;
    shippingInformationDateRequestedGridItem?: GridItemProps;
    shippingDateRequestedTitle?: TypographyPresentationProps;
    shippingDateRequestedText?: TypographyPresentationProps;
    shippingInformationServiceGridItem?: GridItemProps;
    shippingServiceTitle?: TypographyPresentationProps;
    shippingServiceText?: TypographyPresentationProps;
    mainInformationGridContainer?: GridContainerProps;
    mainInformationGridItem?: GridItemProps;
    shippingInformationPoNumberGridItem?: GridItemProps;
    shippingPoNumberTitle?: TypographyProps;
    shippingPoNumberText?: TypographyProps;
    addressesGridItem?: GridItemProps;
    addressesContainer?: GridContainerProps;
    shippingInformationOrderDateGridItem?: GridItemProps;
    shippingOrderDateTitle?: TypographyPresentationProps;
    shippingOrderDateText?: TypographyPresentationProps;
    shippingInformationStatusGridItem?: GridItemProps;
    shippingStatusTitle?: TypographyPresentationProps;
    shippingStatusText?: TypographyPresentationProps;
    shippingInformationSalespersonGridItem?: GridItemProps;
    shippingSalespersonTitle?: TypographyPresentationProps;
    shippingSalespersonText?: TypographyPresentationProps;
    shippingInformationJobNameGridItem?: GridItemProps;
    shippingJobNameTitle?: TypographyProps;
    shippingJobNameText?: TypographyProps;
    notifyWrapper?: GridItemProps;
}

export interface OrderConfirmationOrderInformationStyles {
    OrderInformationGridContainer?: GridContainerProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeading?: TypographyProps;
    orderNumberText?: TypographyPresentationProps;
    shippingInformationGridItem?: GridItemProps;
    vatNumberGridItem?: GridItemProps;
    vatNumberTitleText?: TypographyPresentationProps;
    vatNumberText?: TypographyPresentationProps;
    orderConfirmationShippingInformation?: OrderConfirmationShippingInformationStyles;
    billingInformationGridItem?: GridItemProps;
    orderConfirmationBillingInformation?: OrderConfirmationBillingInformationStyles;
    notesGridItem?: GridItemProps;
    notesTitle: TypographyProps;
    notesDescription?: TypographyPresentationProps;
    orderDetailsGridContainer?: GridContainerProps;
    orderInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    orderNotesGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    // custom
    xOrderConfirmationInfo?: XStyles;
    customNotifyWrapper?:GridItemProps;
}

export const orderInformationStyles: OrderConfirmationOrderInformationStyles = {
    OrderInformationGridContainer: {
        gap: 10,
    },
    orderNumberGridItem: {
        width: 12,
    },
    orderNumberHeading: {
        variant: "h2",
        as: "h1",
        css: css`
            margin-bottom: 5px;
        `,
    },
    shippingInformationGridItem: {
        width: 12,
    },
    vatNumberGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    vatNumberTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    billingInformationGridItem: {
        width: 12,
    },
    notesGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    notesTitle: {
        variant: "h6",
        as: "h2",
        css: css`
            margin-bottom: 5px;
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h6",
            as: "h2",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
    },
    orderInformationGridItems: {
        css: css`
            @media print {
                padding: 5px !important;
            }
        `,
    },
    statusGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    orderDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    poNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    salespersonGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    dueDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    orderNotesGridItem: {
        width: 12,
    },
    orderDetailsGridContainer: {
        css: css`
            padding-left: 5px;
            padding-bottom: 15px;
        `,
    },
    customNotifyWrapper:{
        css: css`
            min-width:100%;
        `,
    }
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = orderInformationStyles;

const OrderConfirmationOrderInformation = ({
    cartState,
    billToState,
    shipToState,
    orderState,
    pickUpWarehouse,
    enableVat,
    language,
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
}: Props) => {
    useEffect(() => {
        if (cartState.isLoading || !cartState.value) {
            return;
        }
        const cart = cartState.value;
        if (!billToState.isLoading && !billToState.value && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }

        if (!shipToState.isLoading && !shipToState.value && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }

        if (!orderState.isLoading && !orderState.value && cart.orderNumber && cart.status === "Submitted") {
            loadOrderByOrderNumber({ orderNumber: cart.orderNumber });
        }
    }, [cartState]);


    const styles = mergeToNew(orderInformationStyles, orderInformationStyles.xOrderConfirmationInfo);

    const cart = cartState.value;
    const billTo = billToState.value;
    const shipTo = shipToState.value;
    const order = orderState.value;
    if (!cart || !billTo || !shipTo) {
        return null;
    }

    return (
        <GridContainer {...styles.OrderInformationGridContainer}>
            <GridItem {...styles.mainInformationGridItem}>
                <GridContainer {...styles.mainInformationGridContainer}>
                    <GridItem {...styles.orderNumberGridItem}>
                        <Typography {...styles.orderNumberHeading}>{`${translate("Order")} #`}</Typography>
                        <Typography {...styles.orderNumberText} data-test-selector="orderConfirmation_orderNumber">
                            {cart.erpOrderNumber || cart.orderNumber}
                        </Typography>
                    </GridItem>
                    {cart.orderDate && (
                        <GridItem {...styles.shippingInformationOrderDateGridItem}>
                            <Typography {...styles.shippingOrderDateTitle}>{translate("Order Date")}</Typography>
                            <Typography {...styles.shippingOrderDateText}>{getLocalizedDateTime({
                                dateTime: new Date(cart.orderDate as Date),
                                language,
                            })}</Typography>
                        </GridItem>
                    )}
                    <GridItem {...styles.shippingInformationPoNumberGridItem}>
                        <Typography {...styles.shippingPoNumberTitle}>{translate("PO #")}</Typography>
                        <Typography {...styles.shippingPoNumberText}>{cart.poNumber}</Typography>
                    </GridItem>
                    {cart.properties?.customerReference1 && (
                        <GridItem {...styles.shippingInformationJobNameGridItem}>
                            <Typography {...styles.shippingJobNameTitle}>{translate("Job Name")}</Typography>
                            <Typography {...styles.shippingJobNameText}>{cart.properties.customerReference1}</Typography>
                        </GridItem>
                    )}
                    {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                        <GridItem {...styles.shippingInformationCarrierGridItem}>
                            <Typography {...styles.shippingCarrierTitle}>{translate("Carrier")}</Typography>
                            <Typography {...styles.shippingCarrierText}>{cart.carrier!.description}</Typography>
                        </GridItem>
                    )}
                    {(cart.requestedDeliveryDate || cart.requestedPickupDate) && (
                        <GridItem {...styles.shippingInformationDateRequestedGridItem}>
                            <Typography {...styles.shippingDateRequestedTitle}>
                                {translate("Date Requested")}
                            </Typography>
                            <Typography
                                {...styles.shippingDateRequestedText}
                                data-test-selector="orderConfirmation_requestedDate"
                            >
                                <LocalizedDateTime
                                    dateTime={
                                        cart.requestedDeliveryDateDisplay || cart.requestedPickupDateDisplay
                                    }
                                />
                            </Typography>
                        </GridItem>
                    )}
                    {cart.status && (
                        <GridItem {...styles.shippingInformationStatusGridItem}>
                            <Typography {...styles.shippingStatusTitle}>{translate("Status")}</Typography>
                            <Typography {...styles.shippingStatusText}>{cart.status}</Typography>
                        </GridItem>
                    )}
                    {cart.salespersonName && (
                        <GridItem {...styles.shippingInformationSalespersonGridItem}>
                            <Typography {...styles.shippingSalespersonTitle}>{translate("Salesperson")}</Typography>
                            <Typography {...styles.shippingSalespersonText}>{cart.salespersonName}</Typography>
                        </GridItem>
                    )}
                    {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                        <GridItem {...styles.shippingInformationServiceGridItem}>
                            <Typography {...styles.shippingServiceTitle}>{translate("Service")}</Typography>
                            <Typography {...styles.shippingServiceText}>{cart.shipVia?.description}</Typography>
                        </GridItem>
                    )}
                    {enableVat && cart.customerVatNumber && (
                        <GridItem {...styles.vatNumberGridItem}>
                            <Typography {...styles.vatNumberTitleText}>{translate("VAT Number")}</Typography>
                            <Typography {...styles.vatNumberText}>{cart.customerVatNumber}</Typography>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
            <GridItem {...styles?.xOrderConfirmationInfo?.notifyWrapper} {...styles.customNotifyWrapper} >
                {cart.properties.shipComplete && (
                    <Notify
                    messageType="warning"
                    body={siteMessage(
                        "Custom_ShipCompleteMessage",
                        "There is no partial shipment. Product will not ship till everything is in stock to ship. ",
                    )}
                    hideCloseButton
                    extendedStyles={{
                        cssOverrides: {
                            toast: css`
                                && {
                                    width: 100%;
                                    margin: 0;
                                    margin-bottom: 10px;
                                }
                                @media print {
                                    display:block;
                                }
                            `,
                        },
                    }}
                    />
                )}
            </GridItem>
            <GridItem {...styles.addressesGridItem}>
                <GridContainer {...styles.addressesContainer}>
                    <GridItem {...styles.shippingInformationGridItem}>
                        <OrderConfirmationShippingInformation
                            cart={cart}
                            shipTo={shipTo}
                            pickUpWarehouse={pickUpWarehouse}
                            extendedStyles={styles.orderConfirmationShippingInformation}
                        />
                    </GridItem>
                    <GridItem {...styles.billingInformationGridItem}>
                        <OrderConfirmationBillingInformation
                            cart={cart}
                            billTo={billTo}
                            extendedStyles={styles.orderConfirmationBillingInformation}
                        />
                    </GridItem>
                </GridContainer>
            </GridItem>
            {cart.notes && (
                <GridItem {...styles.notesGridItem}>
                    <Typography {...styles.notesTitle}>{translate("Notes")}</Typography>
                    <Typography {...styles.notesDescription}>{cart.notes}</Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderConfirmationOrderInformation),
    definition: {
        displayName: "Order Information",
        allowedContexts: ["OrderConfirmationPage"],
        group: "Order Confirmation",
    },
};

export default widgetModule;
